import { Component, Inject, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { ErrorMessage, InfoMessage, Message } from 'src/app/common/messages';
import { dashboardConfig } from 'src/app/dashboards/models';
import { State } from 'src/app/dashboards/state';
import { dashboard, gridItem } from 'src/app/models';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import * as mainActions from 'src/app/state/app.actions';
import { DataTransferService } from 'src/app/services/services/data-transfer.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-reports-dialog-export',
    templateUrl: './reports-dialog-export.component.html',
    styleUrls: ['./reports-dialog-export.component.scss'],
    standalone: false
})

export class ReportsDialogExportComponent implements OnInit, OnChanges {

  type: DialogExportReportToDashboardTypes = DialogExportReportToDashboardTypes.AddToExisting;
  isPublic: boolean = false;
  newDashboardName: string = "";
  widgetTitle: string = "";
  ownDashboards: dashboard[];
  selectedDashboardId: number;
  dashboardsLoading: boolean;
  configLoading: boolean;
  gridItem: gridItem;
  from: any;
  to: any;
  report: any;
  config: dashboardConfig;
  isReportModified: boolean;
  router: Router;
  //dashboardAccess: boolean = false; // is dashboard page accessible

  public notifier = new Subject<number>();

  get ngSelectPlaceholder(): string {
    return this.dashboardsLoading ? this.i18nextPipe.transform('dashboard:Populating') : this.i18nextPipe.transform('dashboard:Select dashboard') + "...";
  };

  get canExport(): boolean {
    return this.isDateTimeSet &&
      (this.type == DialogExportReportToDashboardTypes.CreateNew && this.newDashboardName != "" ||
        this.type == DialogExportReportToDashboardTypes.AddToExisting && this.selectedDashboardId != null);
  };

  get isDateTimeSet(): boolean {
    return this.isDateComplete("to") && this.isDateComplete("from");
  }

  get allowNewDashboardCreation(): boolean {
    return !this.dashboardsLoading && !this.configLoading && this.ownDashboards.length < this.config.maxDashboards;
  }

  constructor(
    private DashboardsService: DashboardService,
    private DataTransferService: DataTransferService,
    private store: Store<State>,
    private i18nextPipe: I18NextPipe,
    public dialogRef: MatDialogRef<ReportsDialogExportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    router: Router) {
    this.isReportModified = data.isReportModified;
    this.report = data.report;
    this.gridItem = data.gridItem;
    //this.dashboardAccess = data.dashboardAccess;
    this.from = this.gridItem.setup.period?.from;
    this.to = this.gridItem.setup.period?.to;
    this.dashboardsLoading = true;
    this.configLoading = true;
    this.router = router;

    DashboardsService.getOwnDashboards().subscribe(
      value => {
        this.ownDashboards = value;
        this.dashboardsLoading = false;
      });

    DashboardsService.getConfiguration().subscribe(
      config => {
        this.config = config;
        this.configLoading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    throw new Error('Method not implemented.');
  }

  ngOnInit(): void {
    this.gridItem.setup.period.type = 1;
    this.gridItem.setup.period.toTime = "24:00";
    this.gridItem.setup.period.fromDateType = 1;
    this.gridItem.isNew = true;
    // hardcoded inconsequential number to represent temporary the gridItem id for the purpose of back-end logic
    this.gridItem.id = 1;
    this.gridItem.colorScheme = 0;
    this.gridItem.setup.reportSubId = 0;
  }

  isDateComplete(modelType) {
    let dateType, dateValue, period;

    period = this.gridItem.setup.period;
    dateType = period[modelType + "DateType"];
    dateValue = period[modelType + "Value"];

    return dateType === 0 || dateType === 1 || dateValue !== null && dateValue !== undefined;
  }

  addButtonClick() {
    this._addWidget();
  }

  addAndSeeDashboardButtonClick(ev: Event) {
    let successCallback;

    ev.preventDefault();

    successCallback = () => {
      this.router.navigateByUrl("dashboard");
    };

    this._addWidget(successCallback);
  }

  showMessage(message: Message) {
    this.store.dispatch(mainActions.addMessage({ message }));
  }

  onFromTimeChange() {
    let id = this.gridItem.setup.period.fromDateType;
    let toId = 0;
    let toValue = null;
    if (id !== 6) {
      switch (id) {
        case 0:
          this.gridItem.setup.period.toDateType = 0;
          this.gridItem.setup.period.to = new Date(this.gridItem.setup.period.from);
          break;
        case 1:
          this.gridItem.setup.period.toDateType = 1;
          this.gridItem.setup.period.to = new Date(this.gridItem.setup.period.from);
          break;
        case 2:
        case 3:
          this.gridItem.setup.period.toDateType = id;
          toId = id;
          this.gridItem.setup.period.fromValue = 0;
          toValue = 6;
          break;
        case 4:
        case 5:
          this.gridItem.setup.period.toDateType = id;
          toId = id;
          this.gridItem.setup.period.fromValue = 1;
          if (this.gridItem.setup.reportId === 1 || this.gridItem.setup.reportId === 271) {
            toValue = 7;
          } else {
            toValue = 32;
          }

          break;
      }

      this.notifier.next(toId);
      this.gridItem.setup.period.toValue = toValue;
    }
  }

  private _addWidget(redirectingCallback: Function = null) {
    let couldNotExportMsg, reportExportedMsg, widgetLimitationMsg, changedThanSavedReportMsg, dashboard: dashboard, gridItems: Array<gridItem>;

    couldNotExportMsg = this.i18nextPipe.transform("messages:Couldn't export the report!");
    reportExportedMsg = this.i18nextPipe.transform('messages:Report Exported!');
    widgetLimitationMsg = this.i18nextPipe.transform("messages:You can not create more widgets in this dashboard.");
    changedThanSavedReportMsg = this.i18nextPipe.transform("messages:The view you added to Dashboard is not identical to the saved report.");

    if (this.type == DialogExportReportToDashboardTypes.AddToExisting) {
      dashboard = this.ownDashboards.find(d => d.id == this.selectedDashboardId);
    } else {
      dashboard = {
        name: this.newDashboardName,
        isPublic: this.isPublic,
        userOwned: true,
        isNew: true,
        isFavorite: false,
        orderNo: 99999,
        visible: true,
        allowNonOwnersToEdit: false,
        tabs: [{
          name: "Default Tab",
          isNew: true,
          gridItems: new Array<gridItem>()
        }]
      };
    }

    if (this.widgetTitle) {
      this.gridItem.title = this.widgetTitle;
    }

    gridItems = dashboard.tabs[0].gridItems;

    if (gridItems.length >= this.config.maxWidgetsPerDashboard) {
      this.showMessage(new ErrorMessage(couldNotExportMsg, widgetLimitationMsg));
      return;
    } else {
      gridItems.push(this.gridItem);
    }

    // we decided not to show this message when it is "Add and See Dashboard", so it should be shown only when the redirectingCallback is lacking 
    if (this.isReportModified && !redirectingCallback) {
      this.showMessage(new InfoMessage(changedThanSavedReportMsg, ""));
    }

    this.DashboardsService.createReportInWidget(dashboard).subscribe(
      (updatedDashboard: dashboard) => {
        if (updatedDashboard) {
          this.showMessage(new InfoMessage(reportExportedMsg, ""));
          this.dialogRef.close(true);

          if (redirectingCallback != null) {
            this.DataTransferService.ExportedDashboardData = {
              id: updatedDashboard.id,
              type: this.type,
              visible: updatedDashboard.visible
            }

            redirectingCallback();
          }
        } else {
          this.showMessage(new ErrorMessage(couldNotExportMsg, ""));
        }
      },
      (error) => {
        this.showMessage(new ErrorMessage(couldNotExportMsg, ""));
      }
    );
  }
}

export enum DialogExportReportToDashboardTypes {
  AddToExisting,
  CreateNew
}
